/**
 * Encounters and choices — GDD §3, §12.
 *
 * An encounter is a data-defined scene with choices. Two things here carry the
 * GDD's weight:
 *
 *  - A failure (face 1) routes to its own outcome branch rather than "nothing
 *    happens". "실패도 콘텐츠다" has to be mechanical or it is just a slogan.
 *  - GDD §17: waiting instead of choosing can reveal a fourth option. So the
 *    choice list is a function of elapsed prompts, not a static array.
 */

import type { DiceFace } from './dice.js';
import { bandOf } from './dice.js';
import type { ResultBand, Trait } from './types.js';
import type { GameState } from './state.js';
import type { EventDraft, EventType, Significance } from './events.js';

export type EncounterKind = 'event' | 'dialogue' | 'puzzle' | 'trap' | 'treasure' | 'shop';

/** A declarative effect. Choices never contain code — see docs/04 §GOD-4 gate. */
export interface EffectSpec {
  readonly type: EventType;
  readonly target?: string;
  readonly significance?: Significance;
  readonly data?: Record<string, string | number | boolean | null>;
  readonly hp?: number;
  readonly gold?: number;
  readonly xp?: number;
  readonly fateDebt?: number;
  readonly setFlag?: string;
  readonly trait?: Trait;
}

export interface ChoiceOutcome {
  readonly text: string;
  readonly effects: readonly EffectSpec[];
  /** Room to move to, if this outcome relocates the player. */
  readonly goto?: string;
}

export interface ChoiceDef {
  readonly id: string;
  readonly label: string;
  /** Condition using the npc.ts grammar, evaluated against world flags/facts. */
  readonly when?: string;
  /** If set, the choice requires a roll and branches on the resulting band. */
  readonly roll?: {
    readonly context: string;
    /** Outcome per band. `failure` is mandatory: failure must be authored content. */
    readonly outcomes: Readonly<Record<ResultBand, ChoiceOutcome>>;
  };
  /** For choices that resolve with no roll. */
  readonly outcome?: ChoiceOutcome;
  /** Requires an item; consumed unless `keepItem`. */
  readonly requiresItem?: string;
  readonly keepItem?: boolean;
  /** Requires a learned fact — Knowledge opening new options (GDD §9). */
  readonly requiresFact?: string;
  /**
   * GDD §17: revealed only after the player has declined to choose this many
   * times. The fourth option that appears if you wait.
   */
  readonly revealAfterWaits?: number;
  readonly sfxId?: string;
}

export interface EncounterDef {
  readonly id: string;
  readonly kind: EncounterKind;
  readonly title: string;
  readonly body: string;
  readonly npcId?: string;
  readonly monsterId?: string;
  readonly choices: readonly ChoiceDef[];
  readonly assetId?: string;
  readonly musicId?: string;
  /** Fired the first time this encounter is seen. */
  readonly onEnter?: readonly EffectSpec[];
  readonly once?: boolean;
}

export interface EncounterRuntime {
  readonly def: EncounterDef;
  /** How many times the player has hesitated (declined to pick) here. */
  waits: number;
  resolvedChoiceId: string | null;
}

export function createEncounterRuntime(def: EncounterDef): EncounterRuntime {
  return { def, waits: 0, resolvedChoiceId: null };
}

export interface ChoiceAvailability {
  readonly choice: ChoiceDef;
  readonly available: boolean;
  /** Why it is greyed out, for honest UI rather than silently hiding options. */
  readonly blockedBy?: 'item' | 'fact' | 'condition' | 'hidden';
}

/**
 * Which choices to show. Hidden-until-waited choices are omitted entirely
 * (revealing a locked mystery option would spoil it); requirement-blocked
 * choices are shown greyed, because knowing you *could* have done it with the
 * right item is what drives a replay.
 */
export function visibleChoices(
  runtime: EncounterRuntime,
  state: GameState,
  evalCondition: (expr: string | undefined) => boolean,
): ChoiceAvailability[] {
  const out: ChoiceAvailability[] = [];
  for (const choice of runtime.def.choices) {
    if (choice.revealAfterWaits !== undefined && runtime.waits < choice.revealAfterWaits) {
      continue;
    }
    if (!evalCondition(choice.when)) {
      out.push({ choice, available: false, blockedBy: 'condition' });
      continue;
    }
    if (choice.requiresItem && !state.inventory.items.includes(choice.requiresItem)) {
      out.push({ choice, available: false, blockedBy: 'item' });
      continue;
    }
    if (choice.requiresFact && !state.world.knownFacts.has(choice.requiresFact)) {
      out.push({ choice, available: false, blockedBy: 'fact' });
      continue;
    }
    out.push({ choice, available: true });
  }
  return out;
}

/** Register a hesitation. Returns choices newly revealed by waiting. */
export function wait(runtime: EncounterRuntime): ChoiceDef[] {
  runtime.waits += 1;
  return runtime.def.choices.filter((c) => c.revealAfterWaits === runtime.waits);
}

export function outcomeFor(choice: ChoiceDef, face: DiceFace | null): ChoiceOutcome | null {
  if (choice.roll) {
    if (face === null) return null;
    return choice.roll.outcomes[bandOf(face)];
  }
  return choice.outcome ?? null;
}

export interface AppliedEffects {
  readonly events: readonly EventDraft[];
  readonly hpDelta: number;
  readonly goldDelta: number;
  readonly xpDelta: number;
  readonly fateDebtDelta: number;
  readonly flags: readonly string[];
  readonly goto: string | null;
}

/**
 * Turn declarative effects into event drafts plus the numeric deltas the session
 * applies directly. Kept separate from the session so it is trivially testable.
 */
export function compileEffects(
  effects: readonly EffectSpec[],
  ctx: { tick: number; room: string | null; rollRef?: number },
  goto?: string,
): AppliedEffects {
  const events: EventDraft[] = [];
  let hpDelta = 0;
  let goldDelta = 0;
  let xpDelta = 0;
  let fateDebtDelta = 0;
  const flags: string[] = [];

  for (const eff of effects) {
    hpDelta += eff.hp ?? 0;
    goldDelta += eff.gold ?? 0;
    xpDelta += eff.xp ?? 0;
    fateDebtDelta += eff.fateDebt ?? 0;
    if (eff.setFlag) flags.push(eff.setFlag);

    const data: Record<string, string | number | boolean | null> = { ...(eff.data ?? {}) };
    if (eff.trait) data['trait'] = eff.trait;

    events.push({
      t: ctx.tick,
      type: eff.type,
      ...(eff.significance ? { significance: eff.significance } : {}),
      ...(eff.target ? { target: eff.target } : {}),
      ...(ctx.room ? { room: ctx.room } : {}),
      ...(ctx.rollRef !== undefined ? { rollRef: ctx.rollRef } : {}),
      data,
    });
  }

  return {
    events,
    hpDelta,
    goldDelta,
    xpDelta,
    fateDebtDelta,
    flags,
    goto: goto ?? null,
  };
}

// ---------------------------------------------------------------------------
// Failure and streak content (GDD §4, §30)
// ---------------------------------------------------------------------------

export interface StreakContent {
  readonly id: string;
  readonly title: string;
  readonly body: string;
  readonly effects: readonly EffectSpec[];
}

/**
 * Bad Luck content. Three low rolls in a row is not punishment, it is a door.
 * These exist in code rather than content JSON because they are a *system*
 * guarantee: every dungeon must have them, so they cannot be forgotten by a
 * content author.
 */
export const BAD_LUCK_CONTENT: readonly StreakContent[] = [
  {
    id: 'badluck.theWatcher',
    title: '누군가 보고 있다',
    body: '연달아 빗나간 손끝을 어둠 속 무언가가 흥미롭게 지켜본다. 그것이 동전 하나를 굴려 보낸다.',
    effects: [
      { type: 'ITEM_GAINED', data: { itemId: 'item.trinket.watchersCoin' }, significance: 'pivotal' },
      { type: 'FACT_LEARNED', data: { factId: 'fact.someoneWatches' } },
    ],
  },
  {
    id: 'badluck.pityOfStone',
    title: '돌의 자비',
    body: '벽이 스스로 갈라져 좁은 틈을 낸다. 운이 없는 자에게만 열리는 길이 있다.',
    effects: [{ type: 'SECRET_DISCOVERED', data: { secretId: 'secret.pityPassage' } }],
  },
  {
    id: 'badluck.debtForgiven',
    title: '탕감',
    body: '운명은 가진 것이 없는 자에게서는 거두지 않는다. 짊어진 무게가 조금 가벼워진다.',
    effects: [{ type: 'ANALYTICS', fateDebt: -12, data: { reason: 'badLuckRelief' } }],
  },
];

/** Fate Chain rewards. Three high rolls running earns escalating flourish. */
export const FATE_CHAIN_CONTENT: readonly StreakContent[] = [
  {
    id: 'chain.handSteadies',
    title: '손이 익는다',
    body: '주사위가 손에 붙는 감각. 다음 한 번은 네 것이다.',
    effects: [{ type: 'ANALYTICS', data: { grant: 'freeReroll' } }],
  },
  {
    id: 'chain.fateNotices',
    title: '운명이 알아본다',
    body: '연속된 행운은 눈에 띈다. 좋은 쪽으로도, 그렇지 않은 쪽으로도.',
    effects: [
      { type: 'FACT_LEARNED', data: { factId: 'fact.fateNoticesYou' } },
      { type: 'ANALYTICS', fateDebt: +8, data: { reason: 'fateChainAttention' } },
    ],
  },
];

/** Fate Events fired by crossing a Fate Debt threshold (GDD §4). */
export const FATE_EVENT_CONTENT: readonly StreakContent[] = [
  {
    id: 'fate.theBill',
    title: '청구서',
    body: '네가 고쳐 쓴 결과들이 한 줄로 서서 기다리고 있다. 하나가 앞으로 나선다.',
    effects: [{ type: 'ANALYTICS', hp: -4, data: { reason: 'fateDebt1' } }],
  },
  {
    id: 'fate.theWeaverLooks',
    title: '직조공이 고개를 든다',
    body: '누군가 실을 세고 있다. 네 이름이 그 목록에 올랐다.',
    effects: [
      { type: 'FACT_LEARNED', data: { factId: 'fact.weaversWatch' }, significance: 'pivotal' },
    ],
  },
  {
    id: 'fate.theDieRemembers',
    title: '주사위가 기억한다',
    body: '손 안의 주사위가 미세하게 따뜻하다. 네가 무엇을 했는지 알고 있다는 듯이.',
    effects: [{ type: 'ANALYTICS', data: { grant: 'diceMemoryAwakened' } }],
  },
  {
    id: 'fate.theLastWarning',
    title: '마지막 경고',
    body: '더 비틀면, 비틀리는 쪽은 세계가 아니라 너다.',
    effects: [{ type: 'ANALYTICS', hp: -8, data: { reason: 'fateDebt4' } }],
  },
];

/** Pick streak content by how deep the streak is, clamped to the list. */
export function streakContentFor(
  table: readonly StreakContent[],
  streakLength: number,
  trigger: number,
): StreakContent {
  const index = Math.min(table.length - 1, Math.floor(streakLength / trigger) - 1);
  return table[Math.max(0, index)]!;
}
